<template>
    <v-layout row wrap>
        <v-flex xs10 offset-xs1 lg8 offset-lg2>
            <v-form ref="form" @submit.prevent="submit">
                <v-card class="pa-3" v-if="!loading">
                    <v-card-text>
                        <v-alert
                            color="warning"
                            icon="warning"
                            value="true"
                            class="mb-3"
                            v-if="!transient"
                        >
                            {{ $t("message.info.substitutionInUse") }}
                        </v-alert>

                        <div class="subheading mb-3">
                            {{ $t("label.basicInfo") }}
                        </div>
                        <v-layout row>
                            <v-flex xs3 lg2>
                                <label class="form-label"
                                    >{{ $t("label.demand") }}*</label
                                >
                            </v-flex>
                            <v-flex xs9 lg10>
                                <v-select
                                    autocomplete
                                    :disabled="disabled || !transient"
                                    v-bind:items="demands"
                                    v-model="form.demand.id"
                                    :rules="validation.required"
                                    :no-data-text="$t('message.warning.noData')"
                                ></v-select>
                            </v-flex>
                        </v-layout>

                        <v-layout row>
                            <v-flex xs3 lg2>
                                <label class="form-label"
                                    >{{ $t("label.originalOwner") }}*</label
                                >
                            </v-flex>
                            <v-flex xs9 lg10>
                                <v-select
                                    autocomplete
                                    :disabled="disabled || !transient"
                                    v-bind:items="persons"
                                    v-model="form.originalOwner.id"
                                    :rules="validation.required"
                                    :no-data-text="$t('message.warning.noData')"
                                ></v-select>
                            </v-flex>
                        </v-layout>

                        <v-layout row v-if="!!this.form.originalOwner.id">
                            <v-flex xs3 lg2>
                                <label class="form-label"
                                    >{{ $t("label.substitute") }}*</label
                                >
                            </v-flex>
                            <v-flex xs9 lg10>
                                <v-select
                                    autocomplete
                                    :disabled="disabled || !transient"
                                    v-bind:items="filteredPersons"
                                    v-model="form.substituteOwner.id"
                                    :rules="validation.required"
                                    :no-data-text="$t('message.warning.noData')"
                                ></v-select>
                            </v-flex>
                        </v-layout>
                    </v-card-text>

                    <v-card-actions v-if="!disabled">
                        <v-btn
                            :disabled="removing"
                            :loading="saving"
                            @click="save"
                            color="primary"
                        >
                            {{ $t("label.save") }}
                        </v-btn>
                        <v-spacer></v-spacer>
                        <v-btn
                            :disabled="saving"
                            :loading="removing"
                            @click="remove"
                            color="error"
                            v-if="!form.isInUse && !!form.id"
                        >
                            {{ $t("label.remove") }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
                <v-card v-else>
                    <app-card-loader></app-card-loader>
                </v-card>
            </v-form>
        </v-flex>
    </v-layout>
</template>

<script>
import constants from "@/helpers/constants";
import utilities from "@/helpers/utilities";
import validation from "@/helpers/validation";
import CardLoader from "@/components/shared/CardLoader";
import DemandOwnerSubstitution from "@/helpers/entities/DemandOwnerSubstitution";

export default {
    data() {
        return {
            constants,
            validation,

            saving: false,
            loading: true,
            removing: false,

            persons: this.$store.getters.personsValueText,
            demands: this.$store.getters.demandsNotCanceledOrClosedValueText,
            form: DemandOwnerSubstitution.build()
        };
    },

    computed: {
        disabled() {
            return !this.$store.getters.isAdmin;
        },
        filteredPersons() {
            const originalOwner = this.$store.getters.personsDictionary[
                this.form.originalOwner.id
            ];
            const substitute = this.$store.getters.personsDictionary[
                this.form.substituteOwner.id
            ];

            // If the new original owner is of a different type (contracted/not contracted)
            //  than the current substitute, remove the substitute
            if (originalOwner.isHired !== (substitute || {}).isHired) {
                this.form.substituteOwner.id = 0;
            }

            let persons = [];
            if (originalOwner.isHired) {
                persons = this.$store.getters.personsHired;
            } else {
                persons = this.$store.getters.personsNotHired;
            }

            if (originalOwner.office && originalOwner.office.id > 0) {
                persons = persons.filter(person => person.office
                                        && person.office.id === originalOwner.office.id);
            }

            if (originalOwner.isOutsourced) {
                persons = persons.filter(person => person.area
                                            && person.area.id === originalOwner.area.id);
            }

            persons = persons
                    .filter(person => person.isActive)
                    .map(({ id, username, name }) => ({
                        value: id,
                        text: `${username} - ${name}`
                    }));

            return persons.filter(i => this.form.originalOwner.id !== i.value);
        },
        transient() {
            return !this.form.id;
        }
    },

    components: {
        appCardLoader: CardLoader
    },

    watch: {
        $route: "fetch",
    },

    created() {
        this.fetch();
    },

    methods: {
        async fetch() {
            this.loading = true;

            const id = this.$route.params.id;
            const demandCode = this.$route.params.demandCode;
            const demandId = this.$route.params.demandId;
            const demandExistsInList = this.demands.find(
                i => i.value === demandId
            );
            if (!utilities.isEmptyOrZero(id)) {
                if (demandExistsInList === undefined) {
                    await this.search(demandCode);
                }
                await this.$store.dispatch("fetchDemandOwnerSubstitution", id);
                this.form = DemandOwnerSubstitution.toForm(
                    this.$store.getters.demandOwnerSubstitution
                );
            }
            this.loading = false;
        },

        async remove() {
            this.removing = true;
            await this.$store.dispatch(
                "removeDemandOwnerSubstitution",
                DemandOwnerSubstitution.toPayload(this.form)
            );
            this.$router.push({ name: "demandOwnerSubstitutionIndex" });
        },

        async save() {
            if (!this.$refs.form.validate()) return;
            try {
                this.saving = true;

                await this.$store.dispatch(
                    "saveOrUpdateDemandOwnerSubstitution",
                    DemandOwnerSubstitution.toPayload(this.form)
                );
                this.$router.push({ name: "demandOwnerSubstitutionIndex" });
            } catch (error) {
                if (error.data && (error.data.code === "generic_error")) {
                    this.saving = false;
                } else {
                    throw error;
                }
            }

        },

        async search(demandCode) {
            await this.$store.dispatch("findDemandByCode", {
                code: demandCode
            });

            const demand = this.$store.getters.demand;
            if (demand.code === demandCode) {
                this.demands = this.$store.getters.demandsValueText;
            } else {
                this.$store.dispatch(
                    "showWarning",
                    this.$t("message.info.demandNotFound")
                );
            }
        }
    },
    mounted() {}
};
</script>



// WEBPACK FOOTER //
// src/components/demandOwnerSubstitutions/Edit.vue